import React, {useLayoutEffect, useState} from 'react';
import {
    IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonModal, IonTitle, IonToolbar,
} from '@ionic/react';
import {closeOutline} from 'ionicons/icons';
import NfcCard from '../shared/nfc/NfcCard';
import {useNfcOperation} from '../shared/nfc/useNfcOperation';
import {NfcSession, scanNfcTag} from '../shared/nfc/webNfc';
import {
    AlarmCouplingError, coupleLobbyAlarm, getAlarmChipText, SESSION_EXPIRED_STATUS,
} from '../../services/alarmCoupling';
import './ZwaailichtKoppelenModal.css';

interface ZwaailichtKoppelenModalProps {
    isOpen: boolean;
    onClose: () => void;
    lobbyId: number;
    token: string | null;
    alarmCoupled?: boolean;
    onCoupled: (signal?: AbortSignal) => Promise<void>;
    onSessionExpired: () => void;
}

const ZwaailichtKoppelenModal: React.FC<ZwaailichtKoppelenModalProps> = (props) => {
    const {isOpen, onClose, lobbyId, token, onCoupled, onSessionExpired} = props;
    const [sending, setSending] = useState(false);
    const operation = useNfcOperation({
        disabled: !isOpen || !token,
        identity: JSON.stringify([lobbyId, token]),
        onSuccess: () => setSending(false),
        onError: (error) => {
            setSending(false);
            if ((error as AlarmCouplingError).status === SESSION_EXPIRED_STATUS) onSessionExpired();
        },
    });
    useLayoutEffect(() => { setSending(false); }, [isOpen, lobbyId, token]);

    const couple = async (session: NfcSession) => {
        const result = await scanNfcTag(session, {allowEmptyRecords: true});
        const secret = getAlarmChipText(result);
        if (session.signal.aborted) return;
        if (localStorage.getItem('jwt') !== token) {
            throw new AlarmCouplingError(SESSION_EXPIRED_STATUS);
        }
        setSending(true);
        await coupleLobbyAlarm({lobbyId, secret, signal: session.signal});
        if (session.signal.aborted || localStorage.getItem('jwt') !== token) return;
        try {
            await onCoupled(session.signal);
        } catch {
            throw new Error('Het alarm is gekoppeld, maar de actuele status kon niet worden opgehaald. Open de lobby opnieuw.');
        }
    };

    const cancel = () => { operation.cancel(); setSending(false); };
    const close = () => { cancel(); onClose(); };

    return (
    <IonModal isOpen={isOpen} onWillDismiss={close} className="zwaailicht-koppelen-modal">
        <IonHeader>
            <IonToolbar>
                <IonTitle>Zwaailicht koppelen</IonTitle>
                <IonButtons slot="end">
                    <IonButton onClick={close} aria-label="Zwaailicht koppelen sluiten">
                        <IonIcon icon={closeOutline} slot="icon-only"/>
                    </IonButton>
                </IonButtons>
            </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
            <NfcCard
                title="Alarm koppelen via NFC"
                description="Scan de alarmchip om het alarm aan deze lobby te koppelen. Dit neemt het alarm over van een andere lobby."
                state={operation.state}
                actionLabel="Alarm koppelen via NFC"
                retryLabel="Alarm koppelen via NFC"
                waitingMessage={sending ? 'Alarm wordt gekoppeld…' : 'Houd je telefoon bij de alarmchip'}
                successMessage={props.alarmCoupled ? 'Alarm gekoppeld' : 'Koppelverzoek verwerkt'}
                disabled={!isOpen || !token}
                onStart={() => operation.start(couple)}
                onCancel={cancel}
            />
        </IonContent>
    </IonModal>
    );
};

export default ZwaailichtKoppelenModal;
